import React from "react";

import Badge from "../Badge/Badge";
import Hyperlink from "../Hyperlink/Hyperlink";
import ResourceStatus, { StatusValues } from "../ResourceStatus/ResourceStatus";
import { ReactComponent as IconExternal } from "../../assets/icons/ripple-external-icon--delwp.svg";

import styles from "./Card.module.css";
import { CellContentProps } from "../SearchResultsTable/BodyCell";
import { componentNanoid } from "../../utils/componentNanoid";
import { RowData } from "../SearchResultsTable/BodyRow";
import FormattedDate from "../Date/Date";
import CombineMetadata from "../Date/CombineMetadata";

export type CardLinkProps = {
    children: React.ReactNode;
    className: string;
    href?: string;
};

export type CardProps = {
    parts: RowData;
};

export const CardDate = ({ data }: { data: string }) => {
    const date = new Intl.DateTimeFormat("en-AU", {
        year: "numeric",
        month: "long",
        day: "numeric",
    }).format(new Date(data));

    return <>{date}</>;
};

const CardContent = ({
    cellDisplay = "text",
    data,
    url,
    label,
    isExternalLink = "false",
    determinationdateOfDecision = "",
    isDfp = "",
}: CellContentProps) => {
    const cellData = data?.trim() ?? "";
    switch (cellDisplay) {
        case "badge":
            return (
                <>
                    {cellData ? (
                        <p>
                            <strong>{label}</strong>
                            <br />
                            <Badge label={cellData} />
                        </p>
                    ) : null}
                </>
            );
        case "status":
            return (
                <>
                    {cellData ? (
                        <div className={styles.status}>
                            <strong>{label}</strong>
                            <br />
                            <ResourceStatus status={cellData as StatusValues} />
                        </div>
                    ) : null}
                </>
            );
        case "textLink":
            return url ? (
                <>
                    <h2 className={styles.heading}>
                        <Hyperlink href={url} className={styles.headingLink}>
                            {cellData}
                            {isExternalLink && (
                                <IconExternal aria-hidden="true" />
                            )}
                        </Hyperlink>
                    </h2>
                    {/* is Dfp for ministerial permits collection only */}
                    {isDfp == "1" ? (
                        <p className={styles.dfp}>
                            <strong>DFP</strong>
                        </p>
                    ) : (
                        ""
                    )}
                </>
            ) : (
                <p className={styles.headingText}>{cellData}</p>
            );
        case "date":
            return (
                <>
                    {cellData ? (
                        <p>
                            <strong>{label}</strong>
                            <br />
                            <FormattedDate label={cellData} />
                        </p>
                    ) : null}
                </>
            );
        case "combineMetadata":
            return (
                <p>
                    {cellData ? (
                        <>
                            <strong>{label}</strong>
                            <br />
                            <CombineMetadata
                                label={cellData}
                                determinationDate={determinationdateOfDecision}
                            />
                        </>
                    ) : null}
                </p>
            );
        default:
            return (
                <>
                    {cellData ? (
                        <div className={styles.description}>
                            <strong>{label}</strong>
                            <br />
                            {cellData.trim().replace("\\n", "\n")}
                        </div>
                    ) : null}
                </>
            );
    }
};

const Card = ({ parts }: CardProps) => (
    <li className={styles.card}>
        {parts?.length &&
            parts.map((part) => (
                <CardContent
                    {...(part as CellContentProps)}
                    key={`card-${componentNanoid()}`}
                />
            ))}
    </li>
);

export default Card;
